// #475: the store of queued copies. Lives on ProgramsInvalidator
// (`copies`); see usePendingProgramCopies for what it holds and why.

type Entry = { keys: readonly string[]; rowId?: string };

export type PendingCopiesStore = {
  subscribe: (fn: () => void) => () => void;
  /** Every key held, as one immutable set (a new one per change). */
  snapshot: () => ReadonlySet<string>;
  /** `rowId`: the host queue's row the copy rides on. A row is held once
   *  per store, ever: the queue tells it again on every change. */
  hold: (keys: readonly string[], rowId?: string) => void;
  /** The row left the queue undelivered: its copy will not come. Drops its
   *  entry and the in-memory holds that answer to any of its keys (the
   *  `queued` answer of the same write). Other rows keep their own. */
  release: (rowId: string) => void;
  /** Drop the entries the list now shows. */
  reconcile: (listed: ReadonlySet<string>) => void;
};

export function createPendingCopiesStore(): PendingCopiesStore {
  let entries: Entry[] = [];
  let snap: ReadonlySet<string> = new Set();
  const seenRows = new Set<string>();
  const subs = new Set<() => void>();
  const commit = (next: Entry[]) => {
    entries = next;
    snap = new Set(next.flatMap((e) => e.keys));
    for (const fn of [...subs]) fn();
  };
  const drop = (gone: (e: Entry) => boolean) => {
    const kept = entries.filter((e) => !gone(e));
    if (kept.length !== entries.length) commit(kept);
  };
  return {
    subscribe: (fn) => {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    snapshot: () => snap,
    hold: (keys, rowId) => {
      if (rowId !== undefined) {
        if (seenRows.has(rowId)) return;
        seenRows.add(rowId);
      }
      if (keys.length > 0) commit([...entries, rowId === undefined ? { keys } : { keys, rowId }]);
    },
    release: (rowId) => {
      const row = entries.find((e) => e.rowId === rowId);
      if (!row) return;
      drop(
        (e) =>
          e === row || (e.rowId === undefined && e.keys.some((k) => row.keys.includes(k))),
      );
    },
    reconcile: (listed) => drop((e) => e.keys.some((k) => listed.has(k))),
  };
}
